import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import type { TermMonthItem } from '@makemydeal/dr-platform-types';
import { isCalculatedColumnSource, resolveByColumn, resolveColumnVerdict } from '@makemydeal/dr-offer-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useSelector } from 'react-redux';
import { useIsAspPaymentGridRedesignActive } from './useIsAspPaymentGridRedesignActive';

/**
 * One definition of "this column's payment came from the calculator, not the deal's lender program", shared by
 * the four rows that flag it: toggle, redesign gate, `decidedColumns` precedence and the settled check.
 * `isSettled` is the caller's own loading notion - Lease scopes it per column, Finance is grid-wide.
 */
export const useIsColumnCalculated = () => {
    const isExplicitLenderP1Enabled = useSelector(featureToggleSelectors.isExplicitLenderP1Enabled);
    const isAspPgRedesignEnabled = useIsAspPaymentGridRedesignActive();
    const columnSources = useSelector(offerReduxSelectors.getPaymentGridColumnSources);
    const decidedColumns = useSelector(offerReduxSelectors.getPaymentGridDecidedColumns);

    return (columnIndex: number, termMonths: number | null | undefined, isSettled: boolean): boolean =>
        Boolean(
            isExplicitLenderP1Enabled &&
                isAspPgRedesignEnabled &&
                isSettled &&
                termMonths &&
                isCalculatedColumnSource(resolveColumnVerdict(decidedColumns, columnSources, columnIndex, termMonths))
        );
};

/** The structural slice of the column view the term row's loading rule needs. */
type TermColumnView = {
    isMasked: boolean;
    requestedColumns: ReadonlySet<number>;
    loadingColumns: ReadonlySet<number>;
    loadingTerms: ReadonlySet<number>;
};

/** The term row's per-column loading rule: Lease scopes it per column, Finance is grid-wide. */
export const isTermColumnLoading = (columnView: TermColumnView, term: TermMonthItem, isLoading: boolean): boolean =>
    columnView.isMasked
        ? resolveByColumn(
              columnView.requestedColumns.size > 0,
              () => columnView.loadingColumns.has(term.key),
              () => term.val != null && columnView.loadingTerms.has(term.val)
          )
        : isLoading;

/**
 * Whether any column in the term row is flagged. The row only re-anchors its cells when one is, so a grid with
 * nothing flagged - the toggle-off case included - keeps the centred layout it has always had.
 */
export const useHasCalculatedColumn = (terms: TermMonthItem[], isLoading: boolean): boolean => {
    const isCalculatedColumn = useIsColumnCalculated();
    const columnView = useSelector(offerReduxSelectors.getPaymentGridColumnView);

    return terms.some((term) => isCalculatedColumn(term.key, term.val, !isTermColumnLoading(columnView, term, isLoading)));
};
